import { useCallback, useEffect, useState } from "react";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import type { ReportQueryInput } from "../types/report.types";

interface UseReportQueryResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Generic hook to run a report GraphQL query. Expects query to use variable $input: ReportQueryInput!.
 * dataKey is the field name in the response (e.g. "reportsOverview", "reportsSalesSummary").
 */
export function useReportQuery<T>(
  query: string,
  dataKey: string,
  input: ReportQueryInput | null
): UseReportQueryResult<T> {
  const { axBe } = useAxiosInterceptor();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!input?.storeCorrelationId) {
      setData(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const variables = {
        input: {
          ...input,
          timezone: input.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      };
      const { data: response } = await axBe.post<{
        data?: Record<string, T>;
        errors?: Array<{ message?: string }>;
      }>(GRAPHQL_ENDPOINT, { query, variables });
      const err = response?.errors?.[0]?.message;
      if (err) {
        setError(err);
        setData(null);
        setLoading(false);
        return;
      }
      const payload = response?.data as Record<string, T> | undefined;
      setData(payload?.[dataKey] ?? null);
    } catch (e: unknown) {
      const res = e as {
        response?: { data?: { errors?: Array<{ message?: string }> } };
        message?: string;
      };
      const message =
        res?.response?.data?.errors?.[0]?.message ?? res?.message ?? "Failed to load report";
      setError(String(message));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [axBe, query, dataKey, input]);

  useEffect(() => {
    if (input?.storeCorrelationId) refetch();
  }, [input?.storeCorrelationId, input?.from, input?.to]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error, refetch };
}
